import React, { useEffect, useMemo, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal
} from '@azure/msal-react';
import {
  AppBar,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  CircularProgress,
  Stack,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme
} from '@mui/material';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { BrowserRouter as Router, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import Home from './pages/Home';
import SettingsPage from './pages/SettingsPage';
import LogsPage from './pages/LogsPage';
import { getMsalConfig, loginRequest } from './authConfig';

const msalInstance = new PublicClientApplication(getMsalConfig());

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0d3b66'
    },
    secondary: {
      main: '#8d5524'
    },
    background: {
      default: '#f6f2ea',
      paper: '#fffdf8'
    },
    success: {
      main: '#1f6f43'
    },
    warning: {
      main: '#9a6700'
    },
    error: {
      main: '#9c2f2f'
    }
  },
  shape: {
    borderRadius: 18
  },
  typography: {
    fontFamily: '"Avenir Next", "Segoe UI", sans-serif',
    h1: {
      fontSize: 'clamp(2.5rem, 5vw, 4.25rem)',
      fontWeight: 700,
      letterSpacing: '-0.03em'
    },
    h2: {
      fontSize: 'clamp(1.8rem, 3vw, 2.4rem)',
      fontWeight: 700,
      letterSpacing: '-0.02em'
    },
    h3: {
      fontWeight: 700
    },
    button: {
      textTransform: 'none',
      fontWeight: 700
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          colorScheme: 'light'
        },
        body: {
          minHeight: '100vh',
          background:
            'radial-gradient(circle at top left, rgba(13,59,102,0.12), transparent 32%), radial-gradient(circle at bottom right, rgba(141,85,36,0.10), transparent 36%), #f6f2ea'
        },
        a: {
          color: 'inherit'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          paddingInline: 20
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    }
  }
});

const navigationItems = [
  { to: '/', label: 'Operations', icon: <DashboardRoundedIcon fontSize="small" />, requiredAny: ['selection.review', 'selection.run', 'vetting.manage', 'exports.manage', 'logs.view'] },
  { to: '/settings', label: 'Settings', icon: <SettingsRoundedIcon fontSize="small" />, requiredAny: ['settings.manage'] },
  { to: '/logs', label: 'Audit Trail', icon: <HistoryRoundedIcon fontSize="small" />, requiredAny: ['logs.view'] }
];

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthenticatedTemplate>
            <AuthenticatedShell />
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <LoginScreen />
          </UnauthenticatedTemplate>
        </Router>
      </ThemeProvider>
    </MsalProvider>
  );
}

function AuthenticatedShell() {
  const { instance, accounts } = useMsal();
  const [authState, setAuthState] = useState({ loading: true, permissions: [], roles: [], displayName: '', error: '' });

  useEffect(() => {
    const loadCurrentUser = async () => {
      setAuthState((current) => ({ ...current, loading: true, error: '' }));

      try {
        const account = accounts[0];
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account
        });

        const response = await fetch('/api/me', {
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`
          }
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Unable to load current user');
        }

        setAuthState({
          loading: false,
          permissions: data.permissions || [],
          roles: data.roles || [],
          displayName: data.displayName || '',
          error: ''
        });
      } catch (error) {
        console.error('Error loading current user', error);
        setAuthState({
          loading: false,
          permissions: [],
          roles: [],
          displayName: '',
          error: error.message || 'Unable to load current user'
        });
      }
    };

    void loadCurrentUser();
  }, [accounts, instance]);

  const canAccess = (requiredAny = []) => requiredAny.length === 0 || requiredAny.some((permission) => authState.permissions.includes(permission));
  const visibleNavigationItems = useMemo(
    () => navigationItems.filter((item) => canAccess(item.requiredAny)),
    [authState.permissions]
  );

  if (authState.loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 4 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Loading your access profile…</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Button
        href="#main-content"
        sx={{
          position: 'absolute',
          left: 16,
          top: 16,
          zIndex: 2000,
          transform: 'translateY(-200%)',
          '&:focus-visible': {
            transform: 'translateY(0)'
          }
        }}
      >
        Skip to main content
      </Button>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backdropFilter: 'blur(18px)',
          backgroundColor: 'rgba(13, 59, 102, 0.92)',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <Toolbar sx={{ minHeight: 80 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 46,
                height: 46,
                borderRadius: '16px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.14)'
              }}
            >
              <GavelRoundedIcon />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.72)', letterSpacing: '0.18em' }}>
                Barbados Registration Department
              </Typography>
              <Typography variant="h6" sx={{ color: 'common.white', lineHeight: 1.1 }}>
                Jury Pool Administration
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' }, mr: 2 }}>
            {visibleNavigationItems.map((item) => (
              <Button
                key={item.to}
                color="inherit"
                component={NavLink}
                to={item.to}
                startIcon={item.icon}
                sx={{
                  color: 'rgba(255,255,255,0.78)',
                  '&.active': {
                    color: 'common.white',
                    backgroundColor: 'rgba(255,255,255,0.12)'
                  }
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          <LogoutButton />
        </Toolbar>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            px: 2,
            pb: 2,
            display: { xs: 'flex', md: 'none' },
            overflowX: 'auto'
          }}
        >
          {visibleNavigationItems.map((item) => (
            <Button
              key={item.to}
              color="inherit"
              component={NavLink}
              to={item.to}
              startIcon={item.icon}
              sx={{
                color: 'rgba(255,255,255,0.78)',
                whiteSpace: 'nowrap',
                '&.active': {
                  color: 'common.white',
                  backgroundColor: 'rgba(255,255,255,0.12)'
                }
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Box
          id="main-content"
          component="main"
          sx={{
            display: 'grid',
            gap: 3
          }}
        >
          {authState.error ? <Alert severity="error">{authState.error}</Alert> : null}
          <Routes>
            <Route
              path="/"
              element={
                canAccess(['selection.review', 'selection.run', 'vetting.manage', 'exports.manage', 'logs.view'])
                  ? <Home permissions={authState.permissions} roles={authState.roles} displayName={authState.displayName} />
                  : <AccessDenied />
              }
            />
            <Route
              path="/settings"
              element={canAccess(['settings.manage']) ? <SettingsPage /> : <AccessDenied />}
            />
            <Route
              path="/logs"
              element={canAccess(['logs.view']) ? <LogsPage /> : <AccessDenied />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Container>
    </Box>
  );
}

function LoginScreen() {
  const { instance } = useMsal();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 3
      }}
    >
      <Box
        sx={{
          width: 'min(960px, 100%)',
          p: { xs: 3, md: 6 },
          borderRadius: 8,
          color: '#fffdf8',
          background:
            'linear-gradient(135deg, rgba(13,59,102,0.98), rgba(27,76,122,0.90) 55%, rgba(141,85,36,0.90))',
          boxShadow: '0 30px 80px rgba(13, 59, 102, 0.28)',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 'auto -12% -24% auto',
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)'
          }}
        />
        <Chip
          icon={<FactCheckRoundedIcon />}
          label="Secure Jury Operations"
          sx={{
            mb: 3,
            color: 'inherit',
            bgcolor: 'rgba(255,255,255,0.10)'
          }}
        />
        <Typography variant="h1" sx={{ maxWidth: 760, textWrap: 'balance' }}>
          Govern every jury pool from intake to final panel.
        </Typography>
        <Typography sx={{ mt: 2, maxWidth: 620, color: 'rgba(255,253,248,0.84)', fontSize: '1.1rem' }}>
          Registration officers generate pools, police complete vetting, and staff can trace every
          invalidation, export, and final outcome through one audited workflow.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<LoginRoundedIcon />}
            onClick={() => instance.loginRedirect(loginRequest)}
          >
            Sign In With Entra ID
          </Button>
          <Typography
            component="p"
            sx={{
              alignSelf: 'center',
              color: 'rgba(255,253,248,0.78)',
              maxWidth: 340
            }}
          >
            Single sign-on is required for administrative use.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

function LogoutButton() {
  const { instance } = useMsal();

  return (
    <Button
      color="inherit"
      variant="outlined"
      startIcon={<LogoutRoundedIcon />}
      onClick={() => instance.logoutRedirect()}
      sx={{
        borderColor: 'rgba(255,255,255,0.28)',
        color: 'common.white',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.48)',
          backgroundColor: 'rgba(255,255,255,0.08)'
        }
      }}
    >
      Sign Out
    </Button>
  );
}

function AccessDenied() {
  return (
    <Alert severity="warning">
      Your current role does not include permission to view this area.
    </Alert>
  );
}

export default App;
